import { Component } from '@theme/component';

/**
 * A tabbed-content section. Each tab block renders both a button and a
 * panel; on mount this component moves the buttons into the `ref="tablist"`
 * container so they sit in the header next to the heading. Tab switching is
 * purely client-side — all panels are rendered server-side; we just toggle
 * which one is visible by id.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [tablist]
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 *
 * @extends {Component<Refs>}
 */
export class TabbedContent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#mountTabs();
    const initial = this.#initialId();
    if (initial != null) this.select(initial);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#mountTabs();
    const initial = this.#initialId();
    if (initial != null) this.select(initial);
  }

  /**
   * Activate the tab whose `data-tab-id` matches `id`.
   * Triggered declaratively via `on:click="/select/<block.id>"`.
   *
   * @param {string} id
   */
  select(id) {
    const tabs = this.refs.tabs ?? [];
    const panels = this.refs.panels ?? [];
    if (!tabs.length || !panels.length) return;

    tabs.forEach((tab) => {
      const active = tab.dataset.tabId === id;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });

    panels.forEach((panel) => {
      panel.hidden = panel.dataset.tabId !== id;
    });
  }

  /**
   * Roving-focus keyboard navigation across the tab buttons
   * (Arrow keys / Home / End). Bound via `on:keydown="/keynav"` on each tab
   * and on the tablist container.
   *
   * @param {KeyboardEvent} event
   */
  keynav(event) {
    const tabs = this.refs.tabs ?? [];
    if (!tabs.length) return;

    const current = tabs.findIndex((t) => t === document.activeElement);
    if (current < 0) return;

    let next;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = tabs[next];
    const nextId = nextTab.dataset.tabId;
    if (nextId != null) this.select(nextId);
    nextTab.focus();
  }

  /**
   * Move each tab button (rendered alongside its panel by the tab block) into
   * the tablist container so the header layout works. Idempotent — buttons
   * already in the tablist are left in place.
   */
  #mountTabs() {
    const tablist = this.refs.tablist;
    const tabs = this.refs.tabs ?? [];
    if (!tablist || !tabs.length) return;

    for (const tab of tabs) {
      if (tab.parentElement !== tablist) {
        tablist.appendChild(tab);
      }
    }
  }

  /**
   * Pick the initially active id: prefer a tab whose server-rendered
   * `aria-selected` is `true`, otherwise the first tab.
   *
   * @returns {string | null}
   */
  #initialId() {
    const tabs = this.refs.tabs ?? [];
    if (!tabs.length) return null;
    const selected = tabs.find((t) => t.getAttribute('aria-selected') === 'true');
    return (selected ?? tabs[0]).dataset.tabId ?? null;
  }
}

if (!customElements.get('tabbed-content-component')) {
  customElements.define('tabbed-content-component', TabbedContent);
}
